// --- React ---
import React, { useState, useMemo, useRef } from 'react';

// --- 다국어 ---
import { useTranslation } from 'react-i18next';

// --- 스타일 · 테마 ---
import { useTheme } from '@/theme';

// --- 공통 UI 컴포넌트 ---
import { Card, Text, CardTitle } from '@/components/ui';
import { DefaultContents } from '@/components/ui';
import { DeviceStatusCard } from '@/components/ui/common/device/DeviceStatusCard';
import { Resizer } from '@/components/common/Resizer';
import { AreaChart } from '@/components/charts/AreaChart';

// --- 페이지 컴포넌트 ---
import { MonitoringSlider } from './MonitoringSlider';
import { PlaybackMap } from './PlaybackMap';

// --- API ---
import { useGetSiteDevices } from '@/api/device/useDevice';
import {
  useSearchPlaybackMonitoring,
  useMonitoringAvailableDates,
  type PlaybackMonitoringData,
} from '@/api/monitoring/useMonitoring';

// --- 스토어 ---
import { useAuthStore } from '@/store/authStore';
import { useSelectionStore } from '@/store/selectionStore';
import { useNotificationStore } from '@/store/notificationStore';

// --- 유틸 ---
import { useCompanySiteScope } from '@/hooks/useCompanySiteScope';
import { parseMonitoringApiError } from '@/utils/errors';
import { mapSensorStatus } from '@/utils/monitoring/sensorStatusMapper';
import { getDeviceImageUrl, LITE_IMAGE_WHOLE } from '@/utils/deviceImageUtils';
import { getDeviceDisplayName } from '@packages/types/app';

// --- i18n ---
import { i18nKeys } from '@/i18n/i18nKeyGen';

// ==================== 유틸 함수 ====================

/** 히스토리 포인트의 status_name·warning·error → AreaChart 선색 (DeviceStatusCard / mapSensorStatus 와 동일 규칙) */
function monitoringPointToChartTone(
  sd: {
    status_name?: string[];
    warning_name?: string;
    error_name?: string;
  } | null
): 'stable' | 'warning' | 'alert' {
  const warningName = sd?.warning_name && sd.warning_name !== 'No Warning' ? sd.warning_name : null;
  const errorName = sd?.error_name && sd.error_name !== 'No Error' ? sd.error_name : null;
  const s = mapSensorStatus(sd?.status_name ?? [], warningName, errorName);
  if (s === 'Stable') return 'stable';
  if (s === 'Warning') return 'warning';
  return 'alert';
}

// point.sensors 객체에서 idx번째 센서 데이터를 꺼내는 헬퍼 (재생 커서 시점 기준)
function getSensorDataAtPoint(point: any, idx: number): any {
  const s = point?.sensors;
  if (s && typeof s === 'object' && !Array.isArray(s)) {
    return Object.values(s)[idx] as any;
  }
  return null;
}

// 재생 커서가 가리키는 시점에 해당 센서가 disabled 상태인지 여부
function isSensorDisabledAtPoint(point: any, idx: number): boolean {
  const sd = getSensorDataAtPoint(point, idx);
  const statusNames: string[] = Array.isArray(sd?.status_name) ? sd.status_name : [];
  return statusNames.includes('Sensor Disabled');
}

// ==================== 컴포넌트 ====================

/**
 * Playback Monitoring Page 컴포넌트
 *
 * 권한별 기능:
 * - SuperAdmin/Admin: Company 선택, Site 선택, Device 선택, 날짜 선택
 * - Company Manager: Site 선택, Device 선택, 날짜 선택
 * - Site Manager: Device 선택, 날짜 선택
 */
const PlaybackPage: React.FC = () => {
  const { isAuthenticated } = useAuthStore();
  const { t } = useTranslation('monitoring');
  const { theme } = useTheme();
  const { showError } = useNotificationStore();

  const { companies, sites, currentSiteId, isAdmin, isSiteManager } = useCompanySiteScope();

  const {
    selectedCompanyId,
    selectedSiteId,
    selectedDeviceId,
    setSelectedCompanyId,
    setSelectedSiteId,
    setSelectedDeviceId,
  } = useSelectionStore();

  // ==================== 상태 관리 ====================

  // 단일 날짜 필터
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);

  // Playback 데이터 상태 (초기값을 빈 객체로 설정하여 UI 영역 표시)
  const [playbackData, setPlaybackData] = useState<PlaybackMonitoringData>(
    {} as PlaybackMonitoringData
  );
  // 현재 선택된 모니터링 데이터 포인트
  const [selectedMonitoringPoint, setSelectedMonitoringPoint] = useState<{
    created_at: string;
    [key: string]: unknown;
  } | null>(null);

  // 리사이저 너비 상태 (왼쪽 영역 너비를 %로 관리)
  const [leftPanelWidth, setLeftPanelWidth] = useState<number>(50); // 기본 50%
  const [isResizing, setIsResizing] = useState<boolean>(false);

  // 리사이저 드래그 핸들러
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  // ==================== 이펙트 ====================

  /** 리사이저 드래그 이벤트 등록/해제 */
  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const container = document.getElementById('playback-layout-container');
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const newLeftWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;

      // 최소/최대 너비 제한 (20% ~ 80%)
      const clampedWidth = Math.max(20, Math.min(80, newLeftWidth));
      setLeftPanelWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // 맵에 전달할 모니터링 데이터를 ref로 관리 (참조 안정화)
  const monitoringDataRef = useRef<any[]>([]);

  // ==================== API 훅 ====================

  // Playback 모니터링 조회 mutation
  const { mutate: fetchPlaybackData, isPending: isLoadingHistory } = useSearchPlaybackMonitoring();

  const { data: devices = [] } = useGetSiteDevices(currentSiteId, {
    enabled: currentSiteId !== null,
  });

  // 선택된 디바이스 정보
  const selectedDevice = devices.find(d => d.id === selectedDeviceId);

  // 데이터가 존재하는 날짜 목록 조회
  const { data: availableDates = [], isLoading: isLoadingDates } = useMonitoringAvailableDates({
    serialNumber: selectedDevice?.serial_number || '',
    deviceType: selectedDevice?.device_type || '',
    enabled: !!selectedDevice,
  });

  // ==================== 이펙트 (데이터 조회) ====================

  /** 날짜 목록 로드 시 가장 최근 날짜 자동 선택 + 즉시 조회 */
  React.useEffect(() => {
    if (availableDates.length > 0 && selectedDevice) {
      const latestDate = availableDates[0]; // DESC 정렬이므로 첫 번째가 최신
      setSelectedDate(latestDate);
      fetchPlaybackData(
        {
          serial_number: selectedDevice.serial_number,
          deviceType: selectedDevice.device_type as 'belllite' | 'bellmini',
          date: latestDate,
        },
        {
          onSuccess: response => {
            monitoringDataRef.current = response.monitoring || [];
            setPlaybackData(response);
            if (response.monitoring && response.monitoring.length > 0) {
              setSelectedMonitoringPoint(response.monitoring[0]);
            }
          },
          onError: error => {
            const e = parseMonitoringApiError(error);
            showError(e.title, e.message);
          },
        }
      );
    }
  }, [availableDates, selectedDevice?.serial_number]);

  /** Company 목록이 로드되면 첫 번째 Company 자동 선택 (Admin만) */
  React.useEffect(() => {
    if (isAdmin && companies.length > 0 && selectedCompanyId === 'all') {
      setSelectedCompanyId(companies[0].id);
    }
  }, [companies, selectedCompanyId, isAdmin]);

  /** Site 목록이 로드되면 첫 번째 Site 자동 선택 (SiteManager 제외) */
  React.useEffect(() => {
    if (!isSiteManager && sites.length > 0 && (!selectedSiteId || selectedSiteId === 'all')) {
      setSelectedSiteId(sites[0].id);
    }
  }, [sites, selectedSiteId, isSiteManager]);

  /** Device 목록이 로드되면 첫 번째 Device 자동 선택 */
  React.useEffect(() => {
    if (devices.length > 0 && (!selectedDeviceId || selectedDeviceId === 'all')) {
      setSelectedDeviceId(devices[0].id);
    }
  }, [devices, selectedDeviceId]);

  // ==================== 핸들러 ====================

  /**
   * 날짜 변경 핸들러
   * - 날짜 선택 시 즉시 히스토리 데이터를 조회한다.
   */
  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    if (selectedDeviceId === 'all' || !selectedDevice) {
      return;
    }
    fetchPlaybackData(
      {
        serial_number: selectedDevice.serial_number,
        deviceType: selectedDevice.device_type as 'belllite' | 'bellmini',
        date,
      },
      {
        onSuccess: response => {
          monitoringDataRef.current = response.monitoring || [];
          setPlaybackData(response);
          if (response.monitoring && response.monitoring.length > 0) {
            setSelectedMonitoringPoint(response.monitoring[0]);
          }
        },
        onError: error => {
          console.error('❌ [API Error] 히스토리 데이터 조회 실패:', error);
          const e = parseMonitoringApiError(error);
          showError(e.title, e.message);
        },
      }
    );
  };

  /** 슬라이더에서 데이터 포인트 선택 시 핸들러 */
  const handleMonitoringPointChange = (dataPoint: any) => {
    setSelectedMonitoringPoint(dataPoint);
  };

  // ==================== 파생 데이터 ====================

  // 맵에 전달할 deviceInfo를 메모이제이션 (불필요한 재렌더링 방지)
  const memoizedDeviceInfo = useMemo(() => {
    if (!playbackData?.device || Object.keys(playbackData).length === 0) return null;
    return {
      name: playbackData.device.name,
      serial_number: playbackData.device.serial_number,
      device_type: playbackData.device.device_type,
      sensor_types: playbackData.device.sensor_types,
      unit: playbackData.device.unit,
      is_connected: playbackData.device.is_connected,
    };
  }, [
    playbackData?.device?.name,
    playbackData?.device?.serial_number,
    playbackData?.device?.device_type,
    playbackData?.device?.unit,
    playbackData?.device?.is_connected,
  ]);

  // monitoringData를 ref에서 가져옴 (항상 같은 참조 유지)
  const memoizedMonitoringData = monitoringDataRef.current;

  // 센서별 차트 데이터를 메모이제이션 (playback tick마다 재계산 방지)
  const sensorChartDataMap = useMemo(() => {
    if (!playbackData.monitoring?.length || !playbackData.device?.sensor_types) return {};

    const formatTime = (dateString: string | Date): string => {
      const date = new Date(dateString);
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${hours}:${minutes}:${seconds}`;
    };

    const result: Record<
      number,
      { time: string; value: number | null; statusTone: 'stable' | 'warning' | 'alert' }[]
    > = {};

    // 센서가 disabled인지 여부는 "현재" config가 아니라 각 시점(item)의 sensor_status로 판단해야 함
    // (재생 중 disabled -> enabled -> disabled로 여러 번 바뀌었을 수 있으므로)
    //
    // 주의: x축이 category 타입이라 배열에서 항목을 통째로 제거하면 남은 포인트들이
    // 재배치되어 실제로는 값이 없던 구간이 마치 데이터가 꽉 찬 것처럼 보인다.
    // 그래서 disabled 시점도 배열에는 남기되 value만 null로 둬서(connectNulls: false)
    // 타임라인 상에 실제로 끊긴 구간으로 표시되게 한다.
    playbackData.device.sensor_types.forEach((_type: string, sensorIndex: number) => {
      const sensorData = playbackData.monitoring
        .map((item: any) => {
          if (!item.created_at) return null;

          const sd =
            item.sensors && typeof item.sensors === 'object'
              ? (Object.values(item.sensors)[sensorIndex] as any)
              : null;

          const statusNames: string[] = Array.isArray(sd?.status_name) ? sd.status_name : [];
          const isDisabledAtThisPoint = statusNames.includes('Sensor Disabled');

          let conc: number | undefined | null = null;
          if (!isDisabledAtThisPoint) {
            if (playbackData.device.device_type === 'belllite') {
              conc = sd?.gas_conc ?? item.gas_conc?.[sensorIndex];
            } else if (playbackData.device.device_type === 'bellmini') {
              if (sensorIndex === 0) {
                conc = sd?.gas_conc ?? item.gas_conc;
              }
            }
          }

          return {
            time: formatTime(item.created_at),
            value: conc === undefined || conc === null ? null : Number(conc),
            statusTone: monitoringPointToChartTone(sd),
          };
        })
        .filter(
          (
            v
          ): v is {
            time: string;
            value: number | null;
            statusTone: 'stable' | 'warning' | 'alert';
          } => v !== null
        )
        .sort((a, b) => a.time.localeCompare(b.time));

      // 하루 종일 disabled였어도(모든 값이 null) 패널 자체는 항상 표시하고,
      // 현재 커서 시점이 disabled면 워터마크로 안내함 (아래 렌더링부 참고)
      result[sensorIndex] = sensorData;
    });

    return result;
  }, [playbackData]);

  // ==================== 렌더링 ====================

  if (!isAuthenticated) {
    return <div>로그인이 필요합니다.</div>;
  }

  return (
    <DefaultContents>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {/* 메인 레이아웃 */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div
            id="playback-layout-container"
            style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              overflow: 'hidden',
              flexDirection: 'row', // 🔥 이거
            }}
          >
            {/* 좌측 패널: 슬라이더 + 디바이스 상태 + 맵 */}
            <div
              style={{
                width: `${leftPanelWidth}%`,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden',
                gap: theme.spacing[4],
              }}
            >
              {/* 모니터링 슬라이더 */}
              <MonitoringSlider
                monitoringData={playbackData.monitoring || []}
                onDataPointChange={handleMonitoringPointChange}
                autoPlay={true}
                initialPlaybackSpeed={1}
                selectedDate={selectedDate}
                onDateChange={handleDateChange}
                availableDates={availableDates}
                isLoadingDates={isLoadingDates}
                isQuerying={isLoadingHistory}
              />

              {/* 디바이스 상태 카드 */}
              {playbackData.device && selectedMonitoringPoint ? (
                (() => {
                  // 디바이스 이미지 URL 생성
                  const deviceImageUrl = getDeviceImageUrl(playbackData.device.device_type, {
                    appId: playbackData.device.app_id,
                    sensorTypes: playbackData.device.sensor_types,
                    isConnected: playbackData.device.is_connected ?? false,
                  });

                  const pointRecord = selectedMonitoringPoint as Record<string, unknown>;
                  const pointGasConc = pointRecord.gas_conc as number[] | number | null | undefined;

                  // sensors 객체에서 idx번째 센서 데이터를 꺼내는 헬퍼
                  const getSensorData = getSensorDataAtPoint;

                  // 센서별 값, 상태, 추가 정보 계산
                  // disabled 여부는 "현재" config가 아니라 선택된 시점(selectedMonitoringPoint)의
                  // sensor_status(status_name)로 판단
                  const sensors =
                    playbackData.device.sensor_types
                      ?.map((type: string, idx: number) => {
                        let status: 'Stable' | 'Warning' | 'Alert' | 'Disabled' = 'Stable';
                        let additionalInfo: string | undefined;

                        // sensors 객체에서 센서 데이터 가져오기 (latest와 동일한 형식)
                        const sd = getSensorData(selectedMonitoringPoint, idx);

                        // sensor_status가 Disabled인 센서는 카드 자체는 유지하되 Disabled로 표시
                        if (isSensorDisabledAtPoint(selectedMonitoringPoint, idx)) {
                          return {
                            type,
                            value: null,
                            unit: '',
                            status: 'Disabled' as const,
                            statusLabel: undefined,
                            additionalInfo: undefined,
                          };
                        }

                        if (sd) {
                          // sensors 객체가 있으면 사용
                          const statusNames = sd.status_name || [];
                          const errorName =
                            sd.error_name && sd.error_name !== 'No Error' ? sd.error_name : null;
                          const warningName =
                            sd.warning_name && sd.warning_name !== 'No Warning'
                              ? sd.warning_name
                              : null;

                          // 공통 매핑 함수 사용 (DeviceInfoCard와 동일)
                          status = mapSensorStatus(statusNames, warningName, errorName);

                          // additionalInfo: 첫 번째 status_name 표시
                          const primaryStatus = statusNames[0] || 'Normal';
                          if (status !== 'Stable' && primaryStatus !== 'Normal') {
                            additionalInfo = primaryStatus;
                          }
                        }

                        // value: sensors 객체 우선, 없으면 raw 배열 fallback
                        const value =
                          playbackData.device.device_type === 'belllite'
                            ? (sd?.gas_conc ??
                              (Array.isArray(pointGasConc) ? pointGasConc[idx] : null) ??
                              null)
                            : idx === 0
                              ? (getSensorData(selectedMonitoringPoint, 0)?.gas_conc ??
                                pointGasConc ??
                                null)
                              : null;

                        // unit: sensors 객체 우선, 없으면 device.unit fallback
                        const unit =
                          sd?.conc_unit ??
                          (playbackData.device.device_type === 'belllite'
                            ? playbackData.device.unit?.[idx] || ''
                            : playbackData.device.unit || '');

                        return {
                          type,
                          value,
                          unit,
                          status,
                          statusLabel:
                            status !== 'Stable' && additionalInfo ? additionalInfo : undefined,
                          additionalInfo: undefined,
                        };
                      })
                      .filter((s): s is NonNullable<typeof s> => s !== null) || [];

                  return (
                    <DeviceStatusCard
                      data-tour="playback-device-status"
                      deviceName={playbackData.device.name ?? ''}
                      serialNumber={playbackData.device.serial_number}
                      deviceType={getDeviceDisplayName(
                        playbackData.device.device_type,
                        playbackData.device.app_id
                      )}
                      timestamp={new Date(selectedMonitoringPoint.created_at).toLocaleString()}
                      temperature={
                        selectedMonitoringPoint.temperature
                          ? `${selectedMonitoringPoint.temperature}°C`
                          : undefined
                      }
                      batteryState={pointRecord.battery_state as number | undefined}
                      deviceImageUrl={deviceImageUrl ?? LITE_IMAGE_WHOLE}
                      sensors={sensors}
                    />
                  );
                })()
              ) : (
                <Card data-tour="playback-device-status" padding={6} layout="flex" gap={4}>
                  <CardTitle>Device Status</CardTitle>
                  <Text size="sm" style={{ color: theme.colors.text.secondary }}>
                    {t(i18nKeys.monitoring.playback_map_waiting)}
                  </Text>
                </Card>
              )}

              {/* 지도 (남은 영역 전체) */}
              <Card padding={6} layout="flex" gap={4} fullHeight data-tour="playback-map">
                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {memoizedDeviceInfo && memoizedMonitoringData.length > 0 ? (
                    <PlaybackMap
                      monitoringData={memoizedMonitoringData}
                      selectedMonitoring={selectedMonitoringPoint}
                      deviceInfo={memoizedDeviceInfo}
                    />
                  ) : (
                    <Text size="sm">
                      {!playbackData.device
                        ? t(i18nKeys.monitoring.playback_map_waiting)
                        : t(i18nKeys.monitoring.playback_no_location)}
                    </Text>
                  )}
                </div>
              </Card>
            </div>

            {/* 리사이저 */}
            <Resizer direction="vertical" onMouseDown={handleMouseDown} isDragging={isResizing} />

            {/* 우측 패널: 데이터 트렌드 차트 */}
            <div
              style={{
                width: `${100 - leftPanelWidth}%`,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden',
              }}
            >
              <Card layout="flex" gap={4} fullHeight>
                <div data-tour="playback-data-trend">
                  <CardTitle>Data Trend</CardTitle>
                </div>
                {/* 스크롤 영역 */}
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                  {/* 데이터 없음 안내 */}
                  {!playbackData.device && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        gap: theme.spacing[6],
                      }}
                    >
                      <Text size="sm">
                        {t(i18nKeys.monitoring.playback_select_device_and_date)}
                      </Text>
                    </div>
                  )}

                  {/* 조회 후 데이터가 없는 경우 */}
                  {playbackData.device &&
                    (!playbackData.monitoring || playbackData.monitoring.length === 0) && (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%',
                          gap: theme.spacing[6],
                        }}
                      >
                        <Text size="sm">{t(i18nKeys.monitoring.playback_no_data)}</Text>
                        <Text size="sm">{t(i18nKeys.monitoring.playback_select_another_date)}</Text>
                      </div>
                    )}

                  {/* 센서별 Gas Concentration 차트 */}
                  {playbackData.device &&
                    playbackData.monitoring &&
                    playbackData.monitoring.length > 0 && (
                      <div>
                        {playbackData.device?.sensor_types &&
                          playbackData.device.sensor_types.length > 0 && (
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: theme.spacing[7],
                              }}
                            >
                              {playbackData.device.sensor_types.map(
                                (sensorType: string, sensorIndex: number) => {
                                  const sensorData = sensorChartDataMap[sensorIndex];
                                  if (!sensorData) return null;

                                  const formatTime = (dateString: string | Date): string => {
                                    const date = new Date(dateString);
                                    const hours = String(date.getHours()).padStart(2, '0');
                                    const minutes = String(date.getMinutes()).padStart(2, '0');
                                    const seconds = String(date.getSeconds()).padStart(2, '0');
                                    return `${hours}:${minutes}:${seconds}`;
                                  };

                                  const selectedTime = selectedMonitoringPoint
                                    ? formatTime(selectedMonitoringPoint.created_at)
                                    : undefined;

                                  const unit =
                                    playbackData.device.device_type === 'belllite'
                                      ? playbackData.device.unit?.[sensorIndex] || ''
                                      : playbackData.device.unit || '';

                                  return (
                                    <div key={sensorIndex}>
                                      <AreaChart
                                        title={`${sensorType} ${unit && `(${unit})`}`}
                                        data={sensorData}
                                        selectedTime={selectedTime}
                                      />
                                    </div>
                                  );
                                }
                              )}
                            </div>
                          )}
                      </div>
                    )}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </DefaultContents>
  );
};

export default PlaybackPage;
